import { faCheck } from "@fortawesome/free-solid-svg-icons/faCheck";
import { faCircleNotch } from "@fortawesome/free-solid-svg-icons/faCircleNotch";
import { faGear } from "@fortawesome/free-solid-svg-icons/faGear";
import { faMinus } from "@fortawesome/free-solid-svg-icons/faMinus";
import { faPlus } from "@fortawesome/free-solid-svg-icons/faPlus";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { AtiraDropdown } from "../../../components/AtiraDropdown";
import { AtiraIcon } from "../../../components/AtiraIcon";
import { TagDot } from "../../../components/TagChip";
import { Text } from "../../../components/Text";
import { TextWithTruncated } from "../../../components/TextWithTruncated";
import { TagEntryBulkDto } from "../../../model/entry/dto/TagEntryBulkDto";
import { entrySliceSelectors } from "../../../redux/entry/entry.selector";
import { useAppSelector } from "../../../redux/store";
import { tagSliceSelectors } from "../../../redux/tag/tag.selector";
import { TestIDs } from "../../../tests/types/testIds";
import { Rounded } from "../../../theme/Rounded";
import { Spacing } from "../../../theme/Spacing";
import { EntryTagsMenuPopup } from "./EntryTagsMenuPopup";
import { useDrawersContext } from "../../tasks/drawers-context";

const ACTIVE_ITEM =
  ".ant-dropdown-menu-item:not(.ant-dropdown-menu-item-disabled):hover &";

// Plain elements rather than Flex, which keeps data attributes to itself
const Option = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.s};
  min-width: 0;
`;

const OptionEnd = styled.span`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
  margin-inline-start: auto;
  flex-shrink: 0;
`;

const OptionStatus = styled(OptionEnd)`
  ${ACTIVE_ITEM} {
    display: none;
  }
`;

const OptionActions = styled(OptionEnd)`
  display: none;

  ${ACTIVE_ITEM} {
    display: flex;
  }
`;

const ActionPill = styled.button<{ $remove?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: 0 ${Spacing.s};
  border-radius: ${Rounded.md};
  border: 1px solid
    ${({ theme, $remove }) => ($remove ? theme.danger : theme.main)};
  background-color: ${({ theme }) => theme.white};
  color: ${({ theme, $remove }) => ($remove ? theme.danger : theme.main)};
  font-size: 0.75rem;
  line-height: 1.5rem;
  cursor: pointer;
  white-space: nowrap;
`;

const Header = styled.div`
  padding: 0 ${Spacing.xs};
`;

type TagState = "all" | "some" | "none";

type TagSummary = {
  state: TagState;
  /** How many selected entries carry the tag, when all of them are loaded */
  count?: number;
};

type EntryBulkTagsDropdownProps = {
  selectedEntriesIds: string[];
  onChange: (change: Pick<TagEntryBulkDto, "add" | "remove">) => Promise<void>;
  children: React.ReactNode;
};

export const EntryBulkTagsDropdown: React.FC<EntryBulkTagsDropdownProps> = ({
  selectedEntriesIds,
  onChange,
  children,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [pendingTagId, setPendingTagId] = useState<string | null>(null);
  const keepOpenRef = useRef(false);

  const { t } = useTranslation();
  const { setTagsManageVisible } = useDrawersContext();

  const tags = useAppSelector(tagSliceSelectors.selectTags);
  const entries = useAppSelector(entrySliceSelectors.selectLatestEntries);

  // Only the current page is loaded. A selection that reaches into other
  // pages can't be seen in full, so no tag counts as on all of it and its
  // count is unknown.
  const getTagSummary = useMemo(() => {
    const selected = entries.filter((entry) =>
      selectedEntriesIds.includes(entry._id),
    );
    const seesAll = selected.length === selectedEntriesIds.length;

    return (tagId: string): TagSummary => {
      const tagged = selected.filter((entry) =>
        entry.tagIds?.includes(tagId),
      ).length;

      if (!tagged) {
        return { state: "none" };
      }

      return {
        state: seesAll && tagged === selected.length ? "all" : "some",
        count: seesAll ? tagged : undefined,
      };
    };
  }, [entries, selectedEntriesIds]);

  const change = useCallback(
    async (tagId: string, dto: Pick<TagEntryBulkDto, "add" | "remove">) => {
      if (pendingTagId) {
        return;
      }

      setPendingTagId(tagId);

      try {
        await onChange(dto);
      } finally {
        setPendingTagId(null);
      }
    },
    [onChange, pendingTagId],
  );

  const onTagClick = useCallback(
    (tagId: string) => {
      keepOpenRef.current = true;
      change(
        tagId,
        getTagSummary(tagId).state === "all"
          ? { remove: [tagId] }
          : { add: [tagId] },
      );
    },
    [change, getTagSummary],
  );

  // Clicking a tag would close the menu, but several are often picked in a row
  const onOpenChange = useCallback(
    (next: boolean, info: { source: "trigger" | "menu" }) => {
      const keepOpen = keepOpenRef.current;
      keepOpenRef.current = false;

      if (!next && info.source === "menu" && keepOpen) {
        return;
      }

      setOpen(next);

      if (!next) {
        setSearch("");
      }
    },
    [],
  );

  const renderStatus = (tagId: string, { state, count }: TagSummary) => {
    if (pendingTagId === tagId) {
      return (
        <AtiraIcon icon={faCircleNotch} color="main" animations={["spin"]} />
      );
    }

    if (state === "all") {
      return <AtiraIcon icon={faCheck} color="main" />;
    }

    if (state === "some") {
      return count ? (
        <Text fontSize="s" color="darkerSub">
          {t("entries.table.bulkHeader.tags.count", {
            count,
            total: selectedEntriesIds.length,
          })}
        </Text>
      ) : (
        <AtiraIcon icon={faMinus} color="main" />
      );
    }

    return null;
  };

  // The pills sit inside the menu item, so they stop the click from reaching
  // it - the item's own click would run its default action as well
  const renderActions = (tagId: string, name: string, state: TagState) => (
    <OptionActions>
      {state !== "all" ? (
        <ActionPill
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            change(tagId, { add: [tagId] });
          }}
          data-testid={TestIDs.entries.table.bulkHeader.tags.add(name)}
        >
          <AtiraIcon icon={faPlus} size="xs" />
          {t("common.add")}
        </ActionPill>
      ) : null}
      {state !== "none" ? (
        <ActionPill
          type="button"
          $remove
          onClick={(e) => {
            e.stopPropagation();
            change(tagId, { remove: [tagId] });
          }}
          data-testid={TestIDs.entries.table.bulkHeader.tags.remove(name)}
        >
          <AtiraIcon icon={faMinus} size="xs" />
          {t("common.remove")}
        </ActionPill>
      ) : null}
    </OptionActions>
  );

  const query = search.trim().toLowerCase();
  const matchingTags = tags.filter((tag) =>
    tag.name.toLowerCase().includes(query),
  );

  const items = [
    ...(matchingTags.length
      ? matchingTags.map((tag) => {
          const summary = getTagSummary(tag._id);

          return {
            key: `tag-${tag._id}`,
            disabled: Boolean(pendingTagId),
            label: (
              <Option
                data-testid={TestIDs.entries.table.bulkHeader.tags.option(
                  tag.name,
                )}
                data-state={summary.state}
              >
                <TagDot $color={tag.color} />
                <TextWithTruncated fitToWidth color="main">
                  {tag.name}
                </TextWithTruncated>
                <OptionStatus>{renderStatus(tag._id, summary)}</OptionStatus>
                {renderActions(tag._id, tag.name, summary.state)}
              </Option>
            ),
            onClick: () => onTagClick(tag._id),
          };
        })
      : [
          {
            key: "tags-empty",
            disabled: true,
            label: (
              <Text fontSize="s" color="darkerSub">
                {t(
                  tags.length
                    ? "entries.table.entry.tags.noMatch"
                    : "entries.table.entry.tags.empty",
                )}
              </Text>
            ),
          },
        ]),
    { type: "divider" as const, key: "tags-divider" },
    {
      key: "tags-manage",
      label: (
        <Option>
          <AtiraIcon icon={faGear} color="main" />
          <Text color="main">{t("entries.table.entry.tags.manage")}</Text>
        </Option>
      ),
      onClick: () => setTagsManageVisible(true),
    },
  ];

  return (
    <AtiraDropdown
      items={items}
      open={open}
      onOpenChange={onOpenChange}
      placement="bottomRight"
      trigger={["click"]}
      destroyOnHidden
      popupRender={(menu) => (
        <EntryTagsMenuPopup
          menu={menu}
          search={search}
          onSearch={setSearch}
          searchTestId={TestIDs.entries.table.bulkHeader.tags.search}
          header={
            <Header>
              <Text fontSize="s" color="darkerSub">
                {t("entries.table.bulkHeader.tags.header", {
                  count: selectedEntriesIds.length,
                })}
              </Text>
            </Header>
          }
        />
      )}
      dataTestId={TestIDs.entries.table.bulkHeader.tags.button}
    >
      {children}
    </AtiraDropdown>
  );
};
